<template>
  <div class="api-lookup-sectioned-form">
    <Loading
      :active.sync="isLoading"
      :background-color="loaderBackgroundColor"
      :color="loaderColor"
      :is-full-page="false"
      loader="dots"
    />
    <div class="api-lookup-form-header">
      <div class="form-row pl-0">
        <VasionInput
          id="api-lookup-name"
          v-model="formFields.name"
          title="API Lookup Name"
          name="api-lookup-name"
          placeholder="API Lookup Name..."
          inputType="top-white"
          required
          :autoFocus="true"
          @input="emitWrapperIsDirty"
        />
      </div>
      <div class="api-lookup-general-section">
        <VasionDropList
          v-slot="slotItem"
          v-model="formFields.method"
          title="Method"
          :dataArray="lists.methods"
          width="100%"
          :showSearchField="true"
          type="plain-list"
          displayName="displayName"
          valueName="value"
          :required="true"
          :showIfEmpty="true"
          @input="onMethodChange"
        >
          {{ slotItem.item.displayName }}
        </VasionDropList>

        <VasionInput
          id="api-lookup-url"
          v-model="formFields.url"
          data-paste-target="true"
          title="URL"
          name="api-lookup-url"
          placeholder="https://"
          inputType="top-white"
          required
          @input="onUrlInputChange"
        />

        <VasionButton
          class="btn-align-bottom"
          classProp="primary"
          :isDisabled="isSendButtonDisabled"
          @vasionButtonClicked="send()"
        >
          Send
        </VasionButton>
      </div>
      <div class="tab-tools-container">
        <div class="left-group">
          <VasionButton
            v-for="(tab, tabIndex) in tabs"
            :id="`tab-al-${tab.name}`"
            :key="tabIndex"
            :classProp="activeTab === tab.name ? 'text-selected' : 'text'"
            @vasionButtonClicked="setActiveTab(tab.name)"
          >
            {{ tab.label }}
          </VasionButton>
        </div>
        <div class="right-group">
          <VasionButton
            class="btn-align-center"
            title="Settings"
            icon="VasionSettingsIcon"
            :isDense="true"
            @vasionButtonClicked="onSettingsClick"
          />
          <md-menu
            class="vasion-menu-btn"
            md-align-trigger
            md-size="big"
            md-direction="bottom-end"
            :mdCloseOnSelect="true"
            :mdCloseOnClick="true"
          >
            <VasionTooltip :text="tooltipTestDataMessage" position="top">
              <md-button
                id="btn-test-data"
                md-menu-trigger
                class="v-btn-primary"
                :disabled="false"
              >
                TEST DATA
                <VasionArrowDropDownIcon />
              </md-button>
            </VasionTooltip>
            <md-menu-content class="dropdown-list-container">
              <md-menu-item @click="showFileUploader = true">
                Document
              </md-menu-item>
              <md-menu-item @click="onTestDataCustomClick">
                Custom
              </md-menu-item>
            </md-menu-content>
          </md-menu>

          <VasionTooltip :text="tooltipExampleDataMessage" position="top">
            <VasionButton
              classProp="secondary"
              :class="{ 'disabled-menu': canSendExampleData }"
              @vasionButtonClicked="onExampleDataClick"
            >
              View Request
            </VasionButton>
          </VasionTooltip>

          <md-menu
            class="vasion-menu-btn"
            md-align-trigger
            md-size="big"
            md-direction="bottom-end"
            :mdCloseOnSelect="true"
            :mdCloseOnClick="true"
          >
            <VasionTooltip :text="tooltipInjectableDataMessage" position="top">
              <md-button
                id="btn-new-configuration-type"
                md-menu-trigger
                class="v-btn-primary"
                :disabled="false"
              >
                Document Data
                <VasionArrowDropDownIcon />
              </md-button>
            </VasionTooltip>
            <md-menu-content class="dropdown-list-container">
              <!-- Object Fields = Document Data -->
              <md-menu-item
                v-for="(objectField, objectFieldIndex) in objectFields"
                :key="objectFieldIndex"
                :title="objectField.title"
                @click="onInjectableDataFieldClick(objectField)"
              >
                {{ objectField.title.substring(0, 24) }}
              </md-menu-item>
              <md-menu-item @click="onUsernameDataClick">
                User Username
              </md-menu-item>
              <md-menu-item @click="onEmailDataClick">User Email </md-menu-item>
              <md-divider />
              <md-menu-item
                :class="{ 'disabled-menu': isComplexDataDisabled }"
                @click="onOCRDataClick"
              >
                OCR Data
              </md-menu-item>
              <md-divider />
              <md-menu-item
                :class="{ 'disabled-menu': isComplexDataDisabled }"
                @click="onLineItemDataClick"
              >
                Line Item Data
              </md-menu-item>
              <md-divider />
              <md-menu-item
                :class="{ 'disabled-menu': isComplexDataDisabled }"
                @click="onDocumentClick"
              >
                Document
              </md-menu-item>
            </md-menu-content>
          </md-menu>
          <md-menu
            v-if="isResponseDataButtonVisible"
            class="vasion-menu-btn btn-active-hover"
            md-align-trigger
            md-size="big"
            md-direction="bottom-end"
            :class="{ 'disabled-menu': isResponseDataButtonDisabled }"
            :mdCloseOnSelect="true"
            :mdCloseOnClick="true"
          >
            <VasionTooltip :text="tooltipResponseDataMessage" position="top">
              <md-button
                id="btn-response-data"
                md-menu-trigger
                class="v-btn-primary"
                :disabled="false"
              >
                Response Data
                <VasionArrowDropDownIcon />
              </md-button>
            </VasionTooltip>
            <md-menu-content class="dropdown-list-container">
              <!-- Response Data Field Keys -->
              <md-menu-item
                v-for="(testFieldKey, testFieldKeyIndex) in testFieldKeys"
                :key="`fieldKey-${testFieldKeyIndex}`"
                @click="onInjectableResponseDataFieldClick(testFieldKey)"
              >
                {{ testFieldKey }}
              </md-menu-item>
            </md-menu-content>
          </md-menu>
        </div>
      </div>
    </div>
    <hr class="tabDivider" />
    <div class="tabs-container">
      <VasionResizableLayout :initialBottomPercentage="20">
        <template #top>
          <KeepAlive>
            <component
              v-bind="getTabComponentProps(currentActiveTabComponent)"
              :key="(currentActiveTabComponent === 'ApiLookupParamsFormSection') ? `${currentActiveTabComponent}-${componentKey}` : `${currentActiveTabComponent}`"
              :is="currentActiveTabComponent"
              class="tab-element"
              :configurationData="configurationData"
              @markAsDirty="emitWrapperIsDirty"
            >
              <template v-if="activeTab === 'body'" v-slot:customActions>
                <md-menu
                  class="vasion-menu-btn"
                  md-align-trigger
                  md-size="big"
                  md-direction="bottom-end"
                  :mdCloseOnSelect="true"
                  :mdCloseOnClick="true"
                >
                  <VasionTooltip
                    :text="tooltipInjectableDataMessage"
                    position="bottom"
                  >
                    <md-button
                      id="btn-new-configuration-type"
                      md-menu-trigger
                      :class="['v-btn-primary', { 'disabled-menu': isComplexDataDisabled }]"
                      :disabled="isComplexDataDisabled"
                    >
                      Document Data
                      <VasionArrowDropDownIcon />
                    </md-button>
                  </VasionTooltip>
                  <md-menu-content class="dropdown-list-container">
                    <!-- Object Fields -->
                    <md-menu-item
                      v-for="(objectField, objectFieldIndex) in objectFields"
                      :key="objectFieldIndex"
                      :title="objectField.title"
                      @click="onInjectableDataFieldClick(objectField)"
                    >
                      {{ objectField.title.substring(0, 24) }}
                    </md-menu-item>
                    <md-menu-item @click="onUsernameDataClick">
                      User Username
                    </md-menu-item>
                    <md-menu-item @click="onEmailDataClick">
                      User Email
                    </md-menu-item>
                    <md-divider />
                    <md-menu-item @click="onOCRDataClick">
                      OCR Data
                    </md-menu-item>
                    <md-divider />
                    <md-menu-item @click="onLineItemDataClick">
                      Line Item Data
                    </md-menu-item>
                    <md-divider />
                    <md-menu-item
                      :class="{ 'disabled-menu': isComplexDataDisabled }"
                      :disabled="isComplexDataDisabled"
                      @click="!isComplexDataDisabled && onDocumentClick()"
                    >
                      Document
                    </md-menu-item>
                  </md-menu-content>
                </md-menu>
              </template>
            </component>
          </KeepAlive>
        </template>
        <template #bottom>
          <VasionCustomizableTextBox
            v-model="response"
            class=""
            title="Response Data"
            :topSideLabel="responseStats"
          />
        </template>
      </VasionResizableLayout>
    </div>
    <ApiLookupSettingsModal
      :sync="showSettingsModal"
      :configurationData="configurationData"
      @closeModal="showSettingsModal = false"
      @setSettings="setApiLookupSettings"
    />
    <ApiLookupExampleDataModal
      :sync="showExampleDataModal"
      :exampleURL="exampleURL"
      :exampleData="exampleData"
      :exampleHeaders="exampleHeaders"
      @closeModal="showExampleDataModal = false"
    />
    <md-dialog
      id="file-uploader"
      :md-click-outside-to-close="false"
      :md-active.sync="showFileUploader"
      class="file-uploader"
    >
      <StartWorkflowFileUpload
        headerText="Choose a document"
        :fileSelectionMode="false"
        :folderSelectionMode="false"
        :isWorkflow="false"
        :handleFileInTheParent="true"
        :showDirectScan="false"
        :showBackButton="false"
        :showLocalDrive="false"
        :objectId="objectID ? Number(objectID) : 0"
        @cancelButtonClick="showFileUploader = false"
        @continueToAttributeForm="continueFromFileSelector"
      />
    </md-dialog>
    <VasionGeneralModal
      id="index-field-list-api-lookup"
      confirmButtonText="DONE"
      customButtonText="BACK"
      rejectButtonText="CANCEL"
      :customButtonDisabled="false"
      :modalTitle="fileName"
      modalType="slot"
      :showConfirmButton="true"
      :showCustomButton="isFromDocumentSelecter"
      :sync="showDocumentFieldsDialog"
      :showTopRightEditButton="isFromDocumentSelecter"
      :showTopRightTrashButton="true"
      @confirmButtonClick="syncTestFields"
      @customButtonClick="goBackToFileSelector"
      @noButtonClick="closeDocumentFieldsDialog"
      @customEditClick="onClearDocumentClick"
      @customTrashClick="onClearDataClick"
    >
      <div class="">
        <IndexFieldList
          id="index-field-list-api-lookup"
          ref="indexFieldListForApiLookup"
          :fields="indexFields"
          :selectorOnly="false"
          :showSubmit="false"
          :useMultiSelectLists="true"
          :enforceFieldSecurity="true"
          :disableAllFields="objectFieldsAreDisabled"
          :isInDocumentView="true"
          @fieldValueUpdated="updateLocalFieldValues"
        />
        <!-- @isValid="markFieldAsValid"
          @notValid="markFieldAsNotValid" -->
        <!-- @clean="markClean"
          @dirty="markDirty" -->
      </div>
    </VasionGeneralModal>
    <VasionGeneralModal
      id="line-item-data-modal"
      confirmButtonText="SAVE"
      rejectButtonText="CANCEL"
      modalTitle="Line Item Tables"
      modalType="slot"
      :showConfirmButton="true"
      :sync="showLineItemDataModal"
      @confirmButtonClick="insertLineItemDataIntoBody"
      @noButtonClick="cancelLineItemData"
    >
      <div>
        <div class="line-item-data-row px-0">
          <VasionInput
            id="line-item-file-name"
            v-model="fileName"
            title="Document Name"
            name="line-item-file-name"
            placeholder="Document Name..."
            inputType="top-white"
            :hoverText="fileName"
            :isDisabled="true"
          />
          <VasionDropList
            v-slot="slotItem"
            v-model="lineItemConfigurationData.table"
            title="Tables"
            :dataArray="lists.lineItemTables"
            width="100%"
            :showSearchField="true"
            type="plain-list"
            displayName="displayName"
            valueName="value"
            :required="true"
            :showIfEmpty="true"
            :isDisabled="isLineItemTablesDisabled"
          >
            {{ slotItem.item.displayName }}
          </VasionDropList>
          <VasionDropList
            v-slot="slotItem"
            v-model="lineItemConfigurationData.column"
            title="Columns"
            :dataArray="
              lists.lineItemTableColumns[
                lineItemConfigurationData.table.value
              ] || []
            "
            width="100%"
            :showSearchField="true"
            type="plain-list"
            displayName="displayName"
            valueName="value"
            :required="true"
            :showIfEmpty="true"
          >
            {{ slotItem.item.displayName }}
          </VasionDropList>
          <VasionButton
            class="btn-align-bottom line-item-data-row-btn"
            classProp="secondary"
            :isDisabled="isAddLineItemDataButtonDisabled"
            @vasionButtonClicked="addTableFromLineItemData"
          >
            Add
          </VasionButton>
        </div>
        <VasionCustomizableTextBox
          v-model="lineItemContent"
          ref="lineItemContent"
          class="line-item-text-editor"
          title=""
          @input="lineItemContentChanged"
        />
      </div>
    </VasionGeneralModal>
  </div>
</template>

<script>
import ApiLookupAuthFormSection from '@/components/forms/api/ApiLookupAuthFormSection.vue'
import ApiLookupBodyFormSection from '@/components/forms/api/ApiLookupBodyFormSection.vue'
import ApiLookupExampleDataModal from '@/components/forms/api/ApiLookupExampleDataModal.vue'
import ApiLookupHeadersFormSection from '@/components/forms/api/ApiLookupHeadersFormSection.vue'
import ApiLookupMappingFormSection from '@/components/forms/api/ApiLookupMappingFormSection.vue'
import ApiLookupParamsFormSection from '@/components/forms/api/ApiLookupParamsFormSection.vue'
import ApiLookupSettingsModal from '@/components/forms/api/ApiLookupSettingsModal.vue'
import IndexFieldList from '@/components/IndexFieldList.vue'
import Loading from 'vue-loading-overlay'
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import StartWorkflowFileUpload from '@/components/workflow/StartWorkflowFileUpload.vue'
import VasionCustomizableTextBox from '@/components/shared/VasionCustomizableTextBox.vue'
import VasionResizableLayout from '@/components/shared/VasionResizableLayout.vue'
import { getUrlParams } from '@/store/helperModules/common.module'
import * as HTTPMethod from '@/static-data/apiLookupHttpMethods.json'

export default {
  name: 'ApiLookupSectionedForm',
  components: {
    ApiLookupAuthFormSection,
    ApiLookupBodyFormSection,
    ApiLookupExampleDataModal,
    ApiLookupHeadersFormSection,
    ApiLookupMappingFormSection,
    ApiLookupParamsFormSection,
    ApiLookupSettingsModal,
    IndexFieldList,
    Loading,
    StartWorkflowFileUpload,
    VasionCustomizableTextBox,
    VasionResizableLayout,
  },
  props: {
    objectID: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      activeTab: null,
      bodyTextElement: null,
      componentKey: 0,
      configurationData: {},
      customTest: false,
      documentId: 0,
      exampleData: '',
      exampleHeaders: [],
      exampleURL: '',
      fileName: '',
      formFields: {
        method: '',
        name: '',
        url: '',
      },
      indexFields: [],
      isApiAuthTypeValid: false,
      isBodyFormJSON: false,
      isLineItemTablesDisabled: false,
      isLoading: false,
      lastFocusedElements: [],
      lineItemConfigurationData: {
        column: '',
        lineItemData: '',
        table: '',
      },
      lineItemContent: '',
      lineItemTables: [],
      lineItemTablesList: [],
      lists: {
        lineItemTableColumns: {},
        lineItemTables: [],
        methods: [
          { displayName: 'GET', value: HTTPMethod.GET },
          { displayName: 'POST', value: HTTPMethod.POST },
          { displayName: 'PUT', value: HTTPMethod.PUT },
          { displayName: 'PATCH', value: HTTPMethod.PATCH },
          { displayName: 'DELETE', value: HTTPMethod.DELETE },
        ],
      },
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
      needToReplaceLine: false,
      objectFieldsAreDisabled: true,
      response: '',
      responseStats: '',
      showDocumentFieldsDialog: false,
      showExampleDataModal: false,
      showFileUploader: false,
      showLineItemDataModal: false,
      showSettingsModal: false,
      tabs: [
        { name: 'auth', label: 'Auth', component: 'ApiLookupAuthFormSection' },
        {
          name: 'params',
          label: 'Params',
          component: 'ApiLookupParamsFormSection',
        },
        {
          name: 'headers',
          label: 'Headers',
          component: 'ApiLookupHeadersFormSection',
        },
        { name: 'body', label: 'Body', component: 'ApiLookupBodyFormSection' },
        {
          name: 'mapping',
          label: 'Mapping',
          component: 'ApiLookupMappingFormSection',
        },
      ],
      testData: {
        documentId: 0,
        sampleData: {},
      },
      testFieldKeys: [],
      tooltipExampleDataMessage:
        'The "View Request" section shows you what your data will look like using the test data you\'ve selected. This button will only become active once the following fields are completed: API Lookup Name, Method, URL, and API Authentication.',
      tooltipInjectableDataMessage:
        'You can easily insert data like object fields, table rows, and OCR results directly into your API parameters and body using document data.',
      tooltipTestDataMessage:
        'Test data provides realistic examples. These examples are real documents submitted to this object. If no documents exist, you can create your own custom data.',
    }
  },
  computed: {
    canSendExampleData() {
      let generalCheck = Boolean(this.formFields.name)
      generalCheck = generalCheck && Boolean(this.formFields.method)
      generalCheck = generalCheck && Boolean(this.formFields.url)
      generalCheck = generalCheck && this.isValidHttpsUrl(this.formFields.url)
      generalCheck = generalCheck && this.isApiAuthTypeValid
      return !generalCheck
    },
    currentActiveTabComponent() {
      const activeTab = this.tabs.find(tab => tab.name === this.activeTab)
      return activeTab ? activeTab.component : null
    },
    objectFields() {
      return this.$store.state.attributeForm.activeIndexForm.fields || []
    },
    isAddLineItemDataButtonDisabled() {
      return !(
        this.lineItemConfigurationData.table &&
        this.lineItemConfigurationData.column
      )
    },
    isFromDocumentSelecter() {
      return this.testData.documentId > 0
    },
    isComplexDataDisabled() {
      return !(this.activeTab === 'body' && this.isBodyFormJSON)
    },
    isSendButtonDisabled() {
      let generalCheck = Boolean(this.formFields.name)
      generalCheck = generalCheck && Boolean(this.formFields.method)
      generalCheck = generalCheck && Boolean(this.formFields.url)
      generalCheck = generalCheck && this.isValidHttpsUrl(this.formFields.url)
      generalCheck = generalCheck && this.isApiAuthTypeValid
      return !generalCheck
    },
    isResponseDataButtonDisabled() {
      return this.testFieldKeys.length === 0
    },
    isResponseDataButtonVisible() {
      const desiredTab = 'mapping'
      return this.activeTab === desiredTab
    },
    tooltipResponseDataMessage() {
      return this.isResponseDataButtonDisabled
        ? 'This feature requires you to be authenticated and have already retrieved response data.'
        : 'Response data (only used for mapping) lets you insert data from API responses into external value mapping fields. You can add multiple fields.'
    },
  },
  mounted() {
    document.addEventListener('focus', this.handleFocus, true)
  },
  beforeDestroy() {
    document.removeEventListener('focus', this.handleFocus, true)
  },
  async created() {
    this.setResponseDataFromRouteParams()
    this.setTestDataFromRouteParams()
    // Get object fields
    await this.getObjectFields(this.objectID)
    // Set default tab
    const defaultTabToSet = 'auth'
    const authTab = this.tabs.find(tab => tab.name === defaultTabToSet)
    this.activeTab = authTab.name
  },
  methods: {
    addTableFromLineItemData() {
      const lineItemContent = `{{${this.lineItemConfigurationData.table.displayName}.${this.lineItemConfigurationData.column.displayName}}}`
      const validElement =
        this.getOldestValidPasteTarget() ||
        this.$refs.lineItemContent.$refs.customTextBox
      if (validElement)
        this.pasteInjectableDataText(validElement, lineItemContent, true)

      this.lineItemConfigurationData.column = ''

      const tableIsSelected = this.lineItemConfigurationData.table != ''
      const bodyContentIsNotEmpty =
        this.$refs.lineItemContent?.$refs?.customTextBox?.value != ''
      this.isLineItemTablesDisabled = tableIsSelected && bodyContentIsNotEmpty
    },
    apiAuthenticationTypeChanged(authType) {
      this.isApiAuthTypeValid = authType?.apiAuthentication?.id >= 0
    },
    bodyFormTypeChanged(isJSON) {
      this.isBodyFormJSON = isJSON
    },
    cancelLineItemData() {
      this.lineItemConfigurationData.table = ''
      this.lineItemConfigurationData.column = ''
      this.lineItemContent = ''
      this.isLineItemTablesDisabled = false

      this.showLineItemDataModal = false
    },
    closeDocumentFieldsDialog() {
      if (Object.keys(this.testData.sampleData).length <= 0) {
        this.indexFields = []
      }
      this.customTest = false
      this.showDocumentFieldsDialog = false
    },
    async continueFromFileSelector(file) {
      // Reset file data
      this.fileName = ''
      this.indexFields = []
      // Check if file is valid
      if (!file || !file.file || !file.file.documentId) {
        this.showFileUploader = false
        return false
      }
      this.testData.documentId = file.file.documentId
      // Get index fields & document details
      let indexFields = await this.$store.dispatch(
        'common/getIndexFieldsForForm',
        this.objectID
      )

      const documentDetails = await this.$store.dispatch(
        'document/getDocumentDetailsWithPayload',
        {
          DocumentId: this.testData.documentId,
          IncludeIndexForm: true,
        }
      )
      // Check if document matches form
      if (documentDetails?.FormId != this.objectID) {
        this.$emit(
          'showSnackbar',
          'API Lookup',
          'The selected document does not match the form.',
          false
        )
        this.testData.documentId = 0
        this.showFileUploader = false
        return false
      }
      // Set index fields values & file name
      await indexFields.map(element => {
        const filedWithValue = documentDetails.IndexFormFields.Values.find(
          field => field.FieldName === element.value
        )
        if (filedWithValue) {
          if (
            filedWithValue.Control === 2 ||
            filedWithValue.Control === 9 ||
            filedWithValue.Control === 10
          ) {
            element.filterValue =
              filedWithValue.DropDownSelectedValues?.Values || []
          } else if (filedWithValue.Control === 3) {
            element.filterValue = filedWithValue?.DateValueUTC || ''
          } else {
            element.filterValue = filedWithValue?.TextValue || ''
          }
        }
        return element
      })
      this.fileName = file.name
      this.indexFields = indexFields
      this.objectFieldsAreDisabled = true

      this.showFileUploader = false
      this.showDocumentFieldsDialog = true
    },
    convertObjToValidParams(obj) {
      const params = []
      for (const key in obj) {
        if (Object.hasOwnProperty.call(obj, key)) {
          const element = obj[key]
          const isEncrypted = element == '********************' ? true : false
          params.push({
            ParamKey: key,
            ParamValue: element,
            isEncrypted: isEncrypted,
          })
        }
      }

      this.configurationData.Parameters = params
      const payload = {
        section: 'params',
        data: {
          Parameters: params || [],
        },
      }
      this.$emit('markAsDirty', payload)
      this.componentKey++
    },
    convertParamsToAnObject(params) {
      const obj = {}
      params.forEach(param => {
        if (!param.ParamKey) return
        obj[param.ParamKey] = param.ParamValue
      })
      return obj
    },
    async createLineItemDropdownsList() {
      let lineItemTablesList = []
      let lineItemTableColumnsList = {}
      this.lineItemTables.map((lineItem, index) => {
        lineItemTablesList.push({
          displayName: lineItem.Tablename,
          value: index,
        })
        lineItemTableColumnsList[index] = []
        lineItem.Headers.map((header, indexHeader) => {
          lineItemTableColumnsList[index].push({
            displayName: header,
            value: indexHeader,
          })
        })
      })
      this.lists.lineItemTables = lineItemTablesList
      this.lists.lineItemTableColumns = lineItemTableColumnsList
    },
    onMethodChange() {
      if (this.configurationData?.Parameters) {
        const newUrl = this.updateUrlWithParams(
          this.formFields.url,
          this.convertParamsToAnObject(this.configurationData.Parameters)
        )
        this.formFields.url = newUrl
      } else {
        const newUrl = this.formFields.url.split('?')[0]
        this.formFields.url = newUrl
      }

      this.emitWrapperIsDirty()
    },
    onUrlInputChange(newUrl) {
      const objParams = getUrlParams(newUrl)
      this.convertObjToValidParams(objParams)

      this.emitWrapperIsDirty(newUrl)
    },
    emitWrapperIsDirty(data) {
      if (data?.section === 'params') {
        this.handleParamsChange(data?.data?.Parameters || {})
      }
      this.$emit('markAsDirty', data)
    },
    async getObjectFields(objectId) {
      await this.$store.dispatch('attributeForm/getFormDetails', objectId)
    },
    getOldestValidPasteTarget() {
      const oldestElement = this.lastFocusedElements[0]

      if (
        oldestElement &&
        oldestElement.getAttribute('data-paste-target') === 'true'
      ) {
        return oldestElement
      }

      return false
    },
    getStringForLineItemData(domElement) {
      const bodyContent = domElement?.value || ''
      const cursorPosition = domElement?.selectionStart || 0

      const bodyContentForJSON = bodyContent.replace(/{{([\s\S]*?)}}/g, '0')
      const isBodyContentValidJSON = this.isValidJSON(bodyContentForJSON)
      if (isBodyContentValidJSON) {
        const emptyArray = this.isCursorInEmptyArray(
          bodyContentForJSON,
          cursorPosition
        )
        if (emptyArray) {
          return {
            lineItemContent: emptyArray + ',',
            position: emptyArray.indexOf('[') + 1,
          }
        }
      }

      const isBodyContentInEmptyVariable = this.isCursorInEmptyVariable(
        bodyContent,
        cursorPosition
      )
      if (isBodyContentInEmptyVariable) {
        return {
          lineItemContent: isBodyContentInEmptyVariable,
          position: isBodyContentInEmptyVariable.indexOf(':') + 1,
        }
      }

      return {
        lineItemContent: '',
        position: 0,
      }
    },
    getTabComponentProps(tabName) {
      if (tabName === 'ApiLookupBodyFormSection') {
        return {
          ref: 'refApiLookupBodyFormSection',
          method: this.formFields.method?.displayName || '',
          bodyFormTypeChanged: this.bodyFormTypeChanged,
        }
      } else if (tabName === 'ApiLookupMappingFormSection') {
        return {
          ref: 'refApiLookupMappingFormSection',
          objectFields: this.objectFields,
        }
      } else if (tabName === 'ApiLookupParamsFormSection') {
        return {
          ref: 'refApiLookupParamsFormSection',
          parameters: this.configurationData?.Parameters || [],
        }
      } else if (tabName === 'ApiLookupHeadersFormSection') {
        return {
          ref: 'refApiLookupHeadersFormSection',
          headers: this.configurationData?.Headers || [],
        }
      } else if (tabName === 'ApiLookupFormData') {
        return {
          ref: 'refApiLookupFormData',
          objectFields: this.objectFields,
        }
      } else if (tabName === 'ApiLookupAuthFormSection') {
        return {
          ref: 'refApiLookupAuthFormSection',
          apiAuthenticationTypeChanged: this.apiAuthenticationTypeChanged,
        }
      }
      return {}
    },
    goBackToFileSelector() {
      this.fileName = ''
      this.indexFields = []
      this.showDocumentFieldsDialog = false
      this.showFileUploader = true
      this.testData.documentId = 0
    },
    handleFocus(event) {
      this.lastFocusedElements.push(event.target)

      if (this.lastFocusedElements.length > 3) {
        this.lastFocusedElements.shift()
      }
    },
    handleParamsChange(params) {
      const paramsObj = this.convertParamsToAnObject(params)
      const newUrl = this.updateUrlWithParams(this.formFields.url, paramsObj)

      if (newUrl !== this.formFields.url && this.isValidHttpsUrl(newUrl)) {
        this.formFields.url = newUrl
      }
    },
    insertLineItemDataIntoBody() {
      const textToInsert = this.$refs.lineItemContent.$refs.customTextBox.value
      const validElement = this.bodyTextElement
      if (validElement && textToInsert)
        this.pasteInjectableDataText(
          validElement,
          textToInsert,
          false,
          this.needToReplaceLine
        )

      this.showLineItemDataModal = false

      this.lineItemConfigurationData.table = ''
      this.lineItemConfigurationData.column = ''
      this.lineItemContent = ''
      this.isLineItemTablesDisabled = false
    },
    isCursorInEmptyArray(jsonString, position) {
      try {
        const formattedJson = JSON.stringify(JSON.parse(jsonString), null, 2)
        const emptyArrayRegex = /"\w+"\s*:\s*\[\s*\]/g

        let match
        while ((match = emptyArrayRegex.exec(formattedJson)) !== null) {
          const [matchText] = match
          const matchStart = match.index
          const matchEnd = matchStart + matchText.length

          if (position >= matchStart && position <= matchEnd) {
            return matchText
          }
        }

        return null
      } catch (error) {
        return null
      }
    },
    isCursorInEmptyVariable(jsonString, position) {
      console.log('jsonString', jsonString)
      console.log('position', position)

      const lines = jsonString.split(/\r?\n/)
      let currentPos = 0

      for (const line of lines) {
        const lineStart = currentPos
        const lineEnd = currentPos + line.length

        if (position >= lineStart && position <= lineEnd) {
          const match = line.match(/^\s*"(\w+)"\s*:\s*(?!\[|\{)(?:,|$)/)
          if (match) return line.trim()
        }
        currentPos = lineEnd + 1
      }

      return ''
    },
    isValidHttpsUrl(url) {
      if (typeof url !== 'string') return false
      
      try {
        const urlObj = new URL(url)
        // Ensures the protocol is HTTPS
        return urlObj.protocol === 'https:'
      } catch {
        return false
      }
    },
    isValidJSON(str) {
      try {
        JSON.parse(str)
        return true
      } catch (error) {
        return false
      }
    },
    lineItemContentChanged(value) {
      const isLineItemContentIsEmpty = value.trim() === ''
      if (isLineItemContentIsEmpty) {
        this.lineItemConfigurationData.table = ''
        this.isLineItemTablesDisabled = !isLineItemContentIsEmpty
      }
    },
    onEmailDataClick() {
      const textToInsert = '{{User_Email}}'
      const validElement = this.getOldestValidPasteTarget()
      if (validElement) this.pasteInjectableDataText(validElement, textToInsert)
    },
    async onExampleDataClick() {
      this.$emit('sendExampleRequestData')
    },
    onInjectableDataFieldClick(objectField) {
      const textToInsert = `{{${objectField.name}}}`
      const validElement = this.getOldestValidPasteTarget()
      if (validElement) this.pasteInjectableDataText(validElement, textToInsert)
    },
    onInjectableResponseDataFieldClick(fieldKey) {
      const textToInsert = `{{${fieldKey}}}`
      const validElement = this.getOldestValidPasteTarget()
      if (!validElement) return
      this.pasteInjectableDataText(validElement, textToInsert)

      this.$nextTick(() => {
        validElement.dispatchEvent(new Event('input'))
      })
    },
    async onTestDataCustomClick() {
      this.isLoading = true
      if (this.testData.documentId > 0) {
        this.objectFieldsAreDisabled = true
      } else {
        this.fileName = 'Custom Mock Data'
        this.customTest = true

        this.objectFieldsAreDisabled = false
      }
      if (!this.indexFields.length > 0) {
        this.indexFields = await this.$store.dispatch(
          'common/getIndexFieldsForForm',
          this.objectID
        )
      }
      if (Object.keys(this.testData.sampleData).length > 0) {
        this.indexFields.forEach(element => {
          if (
            Object.prototype.hasOwnProperty.call(
              this.testData.sampleData,
              element.value
            )
          ) {
            if (
              element.type === 2 ||
              element.type === 9 ||
              element.type === 10
            ) {
              let values = []
              values.push(this.testData.sampleData[element.value])
              element.filterValue = values
            } else {
              element.filterValue = this.testData.sampleData[element.value]
            }
          } else {
            element.filterValue = ''
          }
        })
      }

      this.showDocumentFieldsDialog = true
      this.isLoading = false
    },
    onUsernameDataClick() {
      const textToInsert = '{{User_Username}}'
      const validElement = this.getOldestValidPasteTarget()
      if (validElement) this.pasteInjectableDataText(validElement, textToInsert)
    },
    pasteInjectableDataText(
      validElement,
      textToInsert,
      isFromLineItemData = false,
      replaceLine = false
    ) {
      if (
        validElement.tagName !== 'INPUT' &&
        validElement.tagName !== 'TEXTAREA'
      )
        return false

      const start = validElement.selectionStart
      const end = validElement.selectionEnd

      let currentValue = validElement.value

      if (replaceLine) {
        const before = currentValue.slice(0, start).lastIndexOf('\n') + 1
        const after = currentValue.indexOf('\n', end)
        const lineEnd = after === -1 ? currentValue.length : after
        const lineStart = currentValue
          .slice(before, start)
          .match(/^\s*/)[0].length
        currentValue =
          currentValue.slice(0, before + lineStart) +
          textToInsert +
          currentValue.slice(lineEnd)
        validElement.value = currentValue
        validElement.setSelectionRange(
          before + lineStart + textToInsert.length,
          before + lineStart + textToInsert.length
        )
      } else {
        validElement.value =
          currentValue.slice(0, start) + textToInsert + currentValue.slice(end)
        validElement.setSelectionRange(
          start + textToInsert.length,
          start + textToInsert.length
        )
      }

      if (!isFromLineItemData) {
        this.$refs[`ref${this.currentActiveTabComponent}`]?.setReactiveValue(
          validElement.id,
          validElement.value
        )
      }

      validElement.focus()
      validElement.dispatchEvent(new Event('input'))
    },
    onClearDocumentClick() {
      this.objectFieldsAreDisabled = false
      this.testData.documentId = 0
      this.onTestDataCustomClick()
    },
    onClearDataClick() {
      this.indexFields = []
      this.testData.documentId = 0
      this.testData.sampleData = {}
      this.$refs.indexFieldListForApiLookup.clearIndexFields()
      this.onTestDataCustomClick()
    },
    onDocumentClick() {
      const textToInsert = '{{Document}}'
      const validElement = this.getOldestValidPasteTarget()
      if (validElement) this.pasteInjectableDataText(validElement, textToInsert)
    },
    async onLineItemDataClick() {
      this.bodyTextElement =
        this.getOldestValidPasteTarget() ||
        this.$refs[`refApiLookupBodyFormSection`]?.$refs?.bodyContent?.$refs
          ?.customTextBox

      if (!this.testData.documentId) {
        this.$emit(
          'showSnackbar',
          'API Lookup',
          'Please select a document first.',
          false
        )
        return
      }

      const payload = {
        DocumentId: this.testData.documentId,
        IncludeConfidence: true,
      }
      const result = await this.$store.dispatch(
        'document/getDocumentLineItems2',
        payload
      )
      if (result?.lineItemTables?.length === 0) {
        this.$emit(
          'showSnackbar',
          'API Lookup',
          'No line item data found in the selected document.',
          false
        )
        return
      }
      this.lineItemTables = result.lineItemTables
      await this.createLineItemDropdownsList()

      const lineItemContent = this.getStringForLineItemData(
        this.bodyTextElement
      )
      this.lineItemContent = lineItemContent.lineItemContent

      this.needToReplaceLine =
        lineItemContent.lineItemContent.length > 0 &&
        lineItemContent.position > 0
          ? true
          : false

      this.showLineItemDataModal = true

      setTimeout(() => {
        const customTextBox = this.$refs.lineItemContent.$refs.customTextBox
        customTextBox.setSelectionRange(
          lineItemContent.position,
          lineItemContent.position
        )
        customTextBox.focus()
      }, 500)
    },
    onOCRDataClick() {
      const textToInsert = '{{OCRData}}'
      const validElement = this.getOldestValidPasteTarget()
      if (validElement) this.pasteInjectableDataText(validElement, textToInsert)
    },
    onSettingsClick() {
      this.showSettingsModal = true
    },
    onTestDataClick() {
      console.log('onTestDataClick')
    },
    send() {
      this.$emit('sendTestRequestData')
    },
    setActiveTab(tabName) {
      const tab = this.tabs.find(tab => tab.name === tabName)
      this.activeTab = tab.name
    },
    setApiLookupSettings(settings) {
      const payload = {
        section: 'settings',
        data: settings,
      }
      this.configurationData.IsDefault = settings.defaultLookup
      this.configurationData.FirstRecordOnly = settings.firstRecordOnly
      this.$emit('markAsDirty', payload)
      this.showSettingsModal = false
    },
    setExampleRequestData(data) {
      if (data?.Value === 'false') {
        this.$emit(
          'showSnackbar',
          'API Lookup',
          data.ResultMessage ||
            'An unexpected error occurred. Please try again.',
          false
        )
        return
      }

      const result = JSON.parse(data.ResultMessage)
      this.exampleURL = result.RequestUri
      this.exampleData = result.Content || ''
      this.exampleHeaders = result.Headers
      this.showExampleDataModal = true
    },
    setFormFields(data) {
      this.formFields = {
        method: this.lists.methods.find(
          method => method.value === data.HTTPMethod
        ),
        name: data.ApiLookupName,
        url: data.ApiURL,
      }

      this.configurationData = data

      if (data?.Parameters) {
        const newUrl = this.updateUrlWithParams(
          this.formFields.url,
          this.convertParamsToAnObject(data.Parameters)
        )
        this.formFields.url = newUrl
      }
    },
    setResponseData(data) {
      const { httpStatusCode, httpStatusMessage, responseText, milliseconds, size } = data
      this.responseStats = `Status: <b>${httpStatusCode}</b> ${httpStatusMessage} Time: ${milliseconds} ms Size: ${size} B`
      try {
        this.response = JSON.stringify(JSON.parse(responseText), null, 2)
      } catch {
        this.response = responseText
      }
    },
    setResponseDataFieldKeys(data) {
      this.testFieldKeys = data
    },
    setResponseDataFromRouteParams() {
      const responseData = this.$route.params.responseData || ''
      const responseStats = this.$route.params.responseStats || ''
      if (responseData) {
        this.response = responseData
      }
      if (responseStats) {
        this.responseStats = responseStats
      }
    },
    setTestDataFromRouteParams() {
      const testData = this.$route.params.testData || {}
      if (testData) {
        this.testData.documentId = testData.documentId || 0
        this.testData.sampleData = testData.sampleData || {}
      }
    },
    async syncTestFields() {
      let sampleData = {}
      await this.indexFields.map(field => {
        if (
          Object.prototype.hasOwnProperty.call(field, 'filterValue') &&
          field.filterValue !== null
        ) {
          if (
            typeof field.filterValue === 'string' &&
            field.filterValue.trim() === ''
          )
            return
          if (
            Array.isArray(field.filterValue) &&
            field.filterValue.length === 0
          )
            return
          if (Array.isArray(field.filterValue)) {
            sampleData[field.value] = field.filterValue[0].toString()
            return
          }
          if (typeof field.filterValue === 'number') {
            sampleData[field.value] = field.filterValue.toString()
            return
          }
          sampleData[field.value] = field.filterValue
        }
      })

      const payload = {
        documentId: this.testData?.documentId || 0,
        sampleData: sampleData,
      }
      this.$emit('syncTestRequestData', payload)
      this.testData.sampleData = sampleData

      this.showDocumentFieldsDialog = false
    },
    updateLocalFieldValues(updatedFields) {
      updatedFields.map(updatedField => {
        const index = this.indexFields.findIndex(
          field => field.value === updatedField.name
        )
        this.indexFields[index].filterValue = updatedField.value
      })
    },
    updateUrlWithParams(url, newParams) {
      if (typeof url !== 'string') return ''
      if (typeof newParams !== 'object' || newParams === null) return url

      try {
        const urlObj = new URL(url)
        const baseUrl = urlObj.origin + urlObj.pathname

        let queryString = Object.entries(newParams)
          .map(([key, value]) =>
            value !== null && value !== undefined ? `${key}=${value}` : null
          )
          .filter(Boolean)
          .join('&')

        return queryString ? `${baseUrl}?${queryString}` : baseUrl
      } catch {
        return url
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';

.api-lookup-sectioned-form {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  overflow: auto;
  height: 100%;
}

div.form-row {
  padding: 6px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  grid-gap: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
}

.btn-align-bottom {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.btn-align-bottom ::v-deep button {
  height: 42px;
  align-self: flex-start;
  margin-bottom: 0;
}

.btn-align-center {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.api-lookup-general-section {
  display: flex;
  gap: 8px;

  div:first-child {
    flex: 0 0 150px;
  }

  div:nth-child(2) {
    flex: 1;
  }

  div:last-child {
    ::v-deep button:last-of-type {
      margin-right: 0;
    }
  }
}

.tab-tools-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 16px;

  .left-group,
  .right-group {
    display: flex;
  }

  .right-group {
    gap: 8px;
  }
}

.tab-tools-container ::v-deep button:first-of-type {
  margin-left: 0;
}

.tab-tools-container ::v-deep button:last-of-type {
  margin-right: 0;
}

.tabDivider {
  border-top: 1px solid #cbc4cf;
  border-style: solid;
  margin-top: 4px;
  margin-bottom: 0px;
}

.tabs-container {
  flex: 1 1 auto;
  overflow: auto;
  background-color: $surface;
}
.tab-element {
  height: 100%;
  position: relative;
}
::v-deep .vasion-menu-btn button {
  height: 36px;
  border-radius: 4px;
  font-family: $font-family-book;
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  line-height: 0.71;
  letter-spacing: normal;
  text-align: center;
  z-index: 4;
  margin: 6px 8px;
  color: $primary;
  border: thin solid currentColor;

  .md-button-content {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2px;
  }

  svg {
    fill: $primary;
  }
}
.disabled-menu {
  pointer-events: none;
  opacity: 0.5;
}
.disabled-menu.btn-active-hover {
  pointer-events: unset;
}
.line-item-data-row {
  padding: 6px;
  display: grid;
  grid-template-columns:
    minmax(280px, 1fr) minmax(280px, 1fr) minmax(280px, 1fr)
    100px;
  grid-gap: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
}
::v-deep .line-item-data-row-btn button {
  margin-right: 0px;
  align-self: end;
}
.line-item-text-editor {
  min-height: 250px;
  height: 100%;
  max-height: 450px;
  overflow-y: auto;
  border: 1px solid $on-surface;
  border-radius: 2px;
}
.line-item-text-editor.full-screen {
  max-height: unset;
  border: none;
}
::v-deep .vasion-general-modal {
  min-width: 500px;
  max-width: 550px;

  @media (max-width: 768px) {
    min-width: 100%; // Adjust for tablet screens
  }

  @media (max-width: 480px) {
    min-width: 100%; // Adjust for phone screens
    padding: 0 16px; // Add some padding for smaller screens
  }
}
::v-deep .modal-title-text {
  max-width: 400px;
  text-overflow: ellipsis;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
}
</style>
